import {InvoiceApiDto, UpdateInvoiceApiDto} from "../../api/model/rest";
import AuthenticatedEntity from "../user/AuthenticatedEntity";
import InvoiceItem, {InvoiceItemType} from "./InvoiceItem";
import {none, option, Option} from "ts-option";
import JsonConverter from "../../api/helper/JsonConverter";
import DateUtils from "../../utils/DateUtils";
import NumberUtils from "../../utils/NumberUtils";
import moment = require("moment");

export default class Invoice {

	constructor(public readonly id: string,
		public readonly creationDateTime: moment.Moment,
		public readonly creator: AuthenticatedEntity,
		public readonly customerId: string,
		public readonly customerDisplayName: string,
		public readonly due: number,
		public readonly dueDate: Option<moment.Moment>,
		public readonly invoiceNo: Option<string>,
		public readonly invoiceDate: moment.Moment,
		public readonly items: InvoiceItem[],
		public readonly jobId: Option<string>,
		public readonly partnerBrandId: string,
		public readonly quoteId: Option<string>,
		public readonly referenceNumber: string,
		public readonly sent: boolean,
		public readonly status: string,
		public readonly subtotal: number,
		public readonly tax: Option<number>,
		public readonly total: number,
		public readonly vatRegistered: boolean) {
	}

	static fromJson(json: InvoiceApiDto): Invoice {
		const creationDateTime = json.creationDateTime ? moment(json.creationDateTime) : null;
		const creator = AuthenticatedEntity.fromJson(json.creator);
		const invoiceDate = json.invoiceDate ? moment(json.invoiceDate) : null;
		const dueDate = json.dueDate ? option(moment(json.dueDate)) : none;
		const items = json.items.map(InvoiceItem.fromJson);
		return new Invoice(json.id, creationDateTime, creator, json.customerId, json.customerDisplayName, json.due, dueDate,
			option(json.invoiceNo), invoiceDate, items, option(json.jobId), json.partnerBrandId, option(json.quoteId),
			json.referenceNumber, json.sent, json.status, json.subtotal, option(json.tax), json.total, json.vatRegistered);
	}

	/**
	 * Get invoice status label. Returns array for extended label (see usage).
	 * @returns {string | string[]}
	 */
	getStatusLabel(): string | string[] {
		switch (this.status) {
			case InvoiceStatus.Sent:
				return "SENT TO CUSTOMER";
			case InvoiceStatus.PaidNeedsConfirmation:
				return ["PAID IN FULL", "NEEDS CONFIRMATION"];
			case InvoiceStatus.PaidReleasePending:
				return ["PAID IN FULL", "RELEASE PENDING"];
			default:
				return this.status.replaceUnderscoresWithSpaces();
		}
	}

	/**
	 * Returns item of type LABOUR_AND_PARTS. There should be only one item of this type.
	 */
	getLabourAndPartsItem(): InvoiceItem {
		const [item] = this.items.filter(i => i.type === InvoiceItemType.LABOUR_AND_PARTS);
		return item;
	}

	toJson(): UpdateInvoiceApiDto {
		const json: UpdateInvoiceApiDto = {
			dueDate: this.dueDate.map(dd => dd.format(DateUtils.ISO_DATE_FORMAT)).orNull,
			invoiceDate: this.invoiceDate.format(DateUtils.ISO_DATE_FORMAT),
			customerDisplayName: this.customerDisplayName,
			items: this.items.map(item => item.toJson())
		};
		return JsonConverter.removeEmptyFields(json);
	}

	canPaymentBeAdded() {
		return [InvoiceStatus.Sent, InvoiceStatus.PartiallyPaid, InvoiceStatus.PaidNeedsConfirmation,
			InvoiceStatus.PaidReleasePending, InvoiceStatus.Paid].some(status => status === this.status);
	}

	canBeEdited() {
		return [InvoiceStatus.Draft, InvoiceStatus.Approved, InvoiceStatus.ReadyToSend, InvoiceStatus.ReconciliationUndone,
			InvoiceStatus.Sent].some(status => status === this.status);
	}

	allowPaymentBreakdownWithoutTotalChange() {
		return !(this.status === InvoiceStatus.Paid || this.canBeEdited());
	}

	isPaid() {
		return [InvoiceStatus.PartiallyPaid, InvoiceStatus.PaidNeedsConfirmation, InvoiceStatus.PaidReleasePending,
			InvoiceStatus.Paid].some(status => status === this.status);
	}

	/**
	 * Returns copy of invoice with new items and recalculated subtotal, tax (vat) and total.
	 * @param items
	 */
	withUpdatedItems(items: InvoiceItem[]): Invoice {
		const subtotal = NumberUtils.roundToMaxTwoDecimals(
			items.map(item => NumberUtils.roundToMaxTwoDecimals(
				NumberUtils.getValueBeforeIncreasingByPercent(item.amount, item.taxRate)))
				.reduce((a, b) => a + b, 0));
		const tax = NumberUtils.roundToMaxTwoDecimals(
			items.map(item => item.amount -
				NumberUtils.roundToMaxTwoDecimals(NumberUtils.getValueBeforeIncreasingByPercent(item.amount, item.taxRate)))
				.reduce((a, b) => a + b, 0)
		);
		const total = NumberUtils.roundToMaxTwoDecimals(subtotal + tax);
		const taxToUpdate = tax > 0 ? option(tax) : none;
		return this.copy({items, total, subtotal, tax: taxToUpdate});
	}

	/**
	 * Returns copy of invoice with updated labour and parts item and recalculated subtotal, tax (vat) and total.
	 * Items array contains exactly one LABOUR_AND_PARTS type item and 0 or more USER_DEFINED type items.
	 * @param labourAndPartsItem
	 */
	withUpdatedLabourAndPartsItem(labourAndPartsItem: InvoiceItem): Invoice {
		const userDefinedItems = this.items.filter(i => i.type === InvoiceItemType.USER_DEFINED);
		return this.withUpdatedItems([labourAndPartsItem, ...userDefinedItems]);
	}

	copy({
		id = this.id,
		creationDateTime = this.creationDateTime,
		creator = this.creator,
		customerId = this.customerId,
		customerDisplayName = this.customerDisplayName,
		due = this.due,
		dueDate = this.dueDate,
		invoiceNo = this.invoiceNo,
		invoiceDate = this.invoiceDate,
		items = this.items,
		jobId = this.jobId,
		partnerBrandId = this.partnerBrandId,
		quoteId = this.quoteId,
		referenceNumber = this.referenceNumber,
		sent = this.sent,
		status = this.status,
		subtotal = this.subtotal,
		tax = this.tax,
		total = this.total,
		vatRegistered = this.vatRegistered,
	}) {
		return new Invoice(id, creationDateTime, creator, customerId, customerDisplayName, due, dueDate, invoiceNo, invoiceDate,
			items, jobId, partnerBrandId, quoteId, referenceNumber, sent, status, subtotal, tax, total, vatRegistered);
	}
}

export enum InvoiceStatus {
	Draft = "DRAFT",
	Approved = "APPROVED",
	ReadyToSend = "READY_TO_SEND",
	Sent = "SENT",
	Paid = "PAID",
	PartiallyPaid = "PARTIALLY_PAID",
	PaidNeedsConfirmation = "PAID_NEEDS_CONFIRMATION",
	PaidReleasePending = "PAID_RELEASE_PENDING",
	ReconciliationUndone = "RECONCILIATION_UNDONE"
}
